import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Alert, AlertDescription } from '../ui/alert';
import { useToast } from '../ui/toast';
import { ThumbsUp, ThumbsDown, Save } from 'lucide-react';

const RatingControl = ({ turnIndex, evaluation, onSave }) => {
  const [rating, setRating] = useState(evaluation?.rating ?? null);
  const [comment, setComment] = useState(evaluation?.comment ?? '');
  const [hasChanges, setHasChanges] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const { show, ToastComponent } = useToast();

  useEffect(() => { if (evaluation) { setRating(evaluation.rating); setComment(evaluation.comment ?? ''); } }, [evaluation]);
  useEffect(() => { setHasChanges(rating !== (evaluation?.rating ?? null) || comment !== (evaluation?.comment ?? '')); }, [rating, comment, evaluation]);

  const handleSave = async () => {
    if (rating === null) { setError('Please rate this turn first'); return; }
    if (rating === false && (!comment || !comment.trim())) { setError('Please provide a comment for thumbs down rating'); return; }
    try {
      await onSave({ rating, comment: comment || null });
      setSaved(true); setHasChanges(false);
      show('Rating saved successfully!', 'success');
    } catch { setError('Failed to save rating. Please try again.'); }
  };

  const handleBlur = () => { if (hasChanges && rating !== null) handleSave(); };

  return (
    <div>
      <div className="flex gap-3 items-start">
        <div className="flex gap-1">
          <button onClick={() => { setRating(true); setError(''); }} className={`p-2 rounded-lg border-2 transition-colors ${rating === true ? 'bg-brand-green/10 border-brand-green text-brand-green' : 'border-transparent text-ink-dim hover:bg-brand-green/10'}`}><ThumbsUp className="h-5 w-5" /></button>
          <button onClick={() => { setRating(false); setError(''); }} className={`p-2 rounded-lg border-2 transition-colors ${rating === false ? 'bg-negative/10 border-negative text-negative' : 'border-transparent text-ink-dim hover:bg-negative/10'}`}><ThumbsDown className="h-5 w-5" /></button>
        </div>
        <div className="flex-1">
          {rating !== null && (
            <>
              <div className="mb-1.5">
                <Badge variant={rating ? 'success' : 'destructive'}>{rating ? '👍 Good' : '👎 Bad'}</Badge>
                {evaluation && !saved && !hasChanges && <span className="text-xs text-ink-dim ml-2">Rated</span>}
              </div>
              <Textarea rows={2} placeholder={rating ? "What was good? (optional)" : "What went wrong? (required)"} value={comment} onChange={(e) => setComment(e.target.value)} onBlur={handleBlur} className="text-sm" />
            </>
          )}
          <div className="flex items-center gap-2 mt-1.5">
            <Button size="sm" variant={hasChanges ? 'default' : 'outline'} onClick={handleSave} disabled={!hasChanges || rating === null} className="gap-1.5">
              <Save className="h-3.5 w-3.5" />{hasChanges ? 'Save Rating' : 'Saved'}
            </Button>
            {hasChanges && <span className="text-xs text-ink-dim">Auto-saves on blur</span>}
          </div>
        </div>
      </div>
      {error && <Alert variant="error" className="mt-2"><AlertDescription>{error}</AlertDescription></Alert>}
      {ToastComponent}
    </div>
  );
};

export default RatingControl;
